import type { PropsWithChildren } from 'react';

import { Tooltip } from '@/components/ui/tooltip';
import type {
  PlatformComponentDay as PlatformComponentDayData,
  PlatformComponentDowntime,
} from '@/platform-status/entities/platform-component';
import {
  errorClassBackgroundColor,
  errorClassShown,
  labelFromErrorClass,
} from '@/platform-status/utils/error-class';
import { cls } from '@/utils/cls';
import { formatDate } from '@/utils/format/date';
import { label } from '@/utils/format/label';

import { ErrorClassIcon } from './error-class-icon';

export type PlatformComponentDayProps = {
  day: PlatformComponentDayData;
};

export function PlatformComponentDay({ day }: PlatformComponentDayProps) {
  const errorClass = errorClassShown(day);

  return (
    <PlatformComponentDayTooltip day={day}>
      <div
        className={cls(
          'size-full cursor-pointer rounded-sm bg-primary-500',
          errorClass ? errorClassBackgroundColor[errorClass] : undefined,
        )}
      />
    </PlatformComponentDayTooltip>
  );
}

type PlatformComponentDayTooltipProps = PropsWithChildren<{
  day: PlatformComponentDayData;
}>;

function PlatformComponentDayTooltip({
  day,
  children,
}: PlatformComponentDayTooltipProps) {
  return (
    <Tooltip
      position="bottom"
      delay={100}
      className="w-72 rounded-md border border-gray-200 bg-white p-4 text-gray-900 shadow-sm"
      clickToKeepOpen
      closeOnMouseLeave
      content={
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-900">
            {formatDate(day.date)}
          </div>
          {day.downtimes.length === 0 ? (
            <p className="text-sm text-gray-500">
              No downtime recorded on this day.
            </p>
          ) : (
            <div className="space-y-2">
              {day.downtimes.map((downtime) => (
                <PlatformComponentDayDowntime
                  key={downtime.errorClass}
                  downtime={downtime}
                />
              ))}
            </div>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}

type PlatformComponentDayDowntimeProps = {
  downtime: PlatformComponentDowntime;
};

function PlatformComponentDayDowntime({
  downtime,
}: PlatformComponentDayDowntimeProps) {
  const hours = Math.floor(downtime.durationSec / 3600);
  const minutes = Math.floor((downtime.durationSec % 3600) / 60);

  return (
    <div className="grid grid-cols-[min-content_1fr_min-content_min-content] items-center gap-2 bg-gray-50 p-2">
      <ErrorClassIcon errorClass={downtime.errorClass} />
      <div>{labelFromErrorClass[downtime.errorClass]}</div>
      <div className="whitespace-nowrap">
        {hours} {label(['hr', 'hrs'], hours)}
      </div>
      <div className="whitespace-nowrap">
        {minutes} {label(['min', 'mins'], minutes)}
      </div>
    </div>
  );
}
